import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { ContentRow, TrendCard, TrendsFilter, MediaItem } from '@/types';
import { mediaRows, documentRows, trendCards } from '@/data/mockData';

interface ContentState {
  mediaRows: ContentRow[];
  documentRows: ContentRow[];
  trendCards: TrendCard[];
  trendingOrgItems: MediaItem[];
  trendFeedLoading: boolean;
  trendsFilters: TrendsFilter[];
  activeTrendsFilter: string;
  loading: boolean;
  libraryLoading: boolean;
  error: string | null;
}

const initialState: ContentState = {
  mediaRows: mediaRows,
  documentRows: documentRows,
  trendCards: trendCards,
  trendingOrgItems: [],
  trendFeedLoading: false,
  trendsFilters: [
    { id: 'all', label: 'All platforms' },
    { id: 'tt', label: 'TikTok' },
    { id: 'ig', label: 'Reels' },
    { id: 'li', label: 'LinkedIn' },
    { id: 'yt', label: 'Shorts' },
  ],
  activeTrendsFilter: 'all',
  loading: false,
  libraryLoading: false,
  error: null,
};

// Async thunk — replace mock with real API call when backend is ready
export const fetchMediaRows = createAsyncThunk('content/fetchMediaRows', async (_, { rejectWithValue }) => {
  try {
    // const response = await mediaService.getRows();
    // return response.data;
    return mediaRows; // placeholder
  } catch (err) {
    return rejectWithValue('Failed to load media rows');
  }
});

export const fetchTrends = createAsyncThunk('content/fetchTrends', async (_, { rejectWithValue }) => {
  try {
    // const response = await trendsService.getCards();
    // return response.data;
    return trendCards; // placeholder
  } catch (err) {
    return rejectWithValue('Failed to load trends');
  }
});

// ─── Tab Feed Thunks ──────────────────────────────────────────────────────────
// Removed: fetchTabNew, fetchTabTrending, fetchTabPodcast, fetchTabTestimonial, fetchTabSocialShorts
// The home screen now uses sectionsSlice.fetchLibrary (GET /api/v3/library) which
// calls dispatch(setMediaRowsFromLibrary(rows)) to populate content.mediaRows.

const contentSlice = createSlice({
  name: 'content',
  initialState,
  reducers: {
    setActiveTrendsFilter(state, action: PayloadAction<string>) {
      state.activeTrendsFilter = action.payload;
    },
    /** Called by sectionsSlice.fetchLibrary to sync library rows into mediaRows */
    setMediaRowsFromLibrary(state, action: PayloadAction<ContentRow[]>) {
      state.mediaRows = action.payload;
      state.libraryLoading = false;
    },
    setLibraryLoading(state, action: PayloadAction<boolean>) {
      state.libraryLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMediaRows.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchMediaRows.fulfilled, (state, action) => {
        state.loading = false;
        state.mediaRows = action.payload as ContentRow[];
      })
      .addCase(fetchMediaRows.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTrends.fulfilled, (state, action) => {
        state.trendCards = action.payload as TrendCard[];
      });
  },
});

export const { setActiveTrendsFilter, setMediaRowsFromLibrary, setLibraryLoading } = contentSlice.actions;
export default contentSlice.reducer;
